import React, { createContext, useState, useContext, ReactNode, useCallback, useRef, useEffect } from 'react';

type PaymentSidebarData = {
  storeId: string;
  storeName: string;
  total: number;
  cartId: string;
  sharedToken?: string | null;
  isDigital?: boolean;
  rentalMetadata?: {
    mode: 'nueva' | 'renovar';
    storeId: string | null;
    paidDays: number;
  } | null;
} | null;

type CheckoutContextType = {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  openSidebar: (options?: { cartOnly?: boolean; preferredStoreId?: string }) => void;
  closeSidebar: () => void;
  closeWithDelay: (delayMs?: number) => void;
  cancelCloseDelay: () => void;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  paymentSidebarOpen: boolean;
  openPaymentSidebar: (data: NonNullable<PaymentSidebarData> ) => void;
  closePaymentSidebar: () => void;
  paymentSidebarData: PaymentSidebarData;
  cartOnlyMode: boolean;
  preferredStoreId: string | null;
  preferredStoreName: string | null;
  setPreferredStoreId: (storeId: string | null) => void;
  setPreferredStoreName: (storeName: string | null) => void;
};

export const CheckoutContext = createContext<CheckoutContextType | undefined>(undefined);

export const CheckoutProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [paymentSidebarOpen, setPaymentSidebarOpen] = useState(false);
  const [paymentSidebarData, setPaymentSidebarData] = useState<PaymentSidebarData>(null);
  const [cartOnlyMode, setCartOnlyMode] = useState(false);
  const [preferredStoreId, setPreferredStoreId] = useState<string | null>(null);
  const [preferredStoreName, setPreferredStoreName] = useState<string | null>(null);
  const closeTimerRef = useRef<NodeJS.Timeout|null>(null);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);

  const openSidebar = useCallback((options?: { cartOnly?: boolean; preferredStoreId?: string }) => {
    setCartOnlyMode(options?.cartOnly ?? false);
    if (options?.preferredStoreId !== undefined) {
      setPreferredStoreId(options.preferredStoreId || null);
    }
    // Forzar recálculo del header inmediatamente antes de abrir
    window.dispatchEvent(new Event('recalculate-header'));
    // Pequeño delay para asegurar que el recálculo se complete
    requestAnimationFrame(() => {
      setSidebarOpen(true);
      window.dispatchEvent(new CustomEvent('sidebar-opened', { detail: { type: 'checkout' } }));
    });
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
    setCartOnlyMode(false);
  }, []);

  const cancelCloseDelay = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const closeWithDelay = useCallback((delayMs: number = 1000) => {
    cancelCloseDelay();
    closeTimerRef.current = setTimeout(() => {
      setSidebarOpen(false);
      closeTimerRef.current = null;
    }, delayMs);
  }, [cancelCloseDelay]);

  const openPaymentSidebar = useCallback((data: NonNullable<PaymentSidebarData>) => {
    setPaymentSidebarData(data);
    setPaymentSidebarOpen(true);
  }, []);

  const closePaymentSidebar = useCallback(() => {
    setPaymentSidebarOpen(false);
    // Clear data after animation
    setTimeout(() => {
      setPaymentSidebarData(null);
    }, 300);
  }, []);

  // Listen for other sidebars opening
  useEffect(() => {
    const handleSidebarOpened = (event: CustomEvent) => {
      if (event.detail.type !== 'checkout') {
        cancelCloseDelay();
        setSidebarOpen(false);
      }
    };
    window.addEventListener('sidebar-opened', handleSidebarOpened as EventListener);
    return () => {
      window.removeEventListener('sidebar-opened', handleSidebarOpened as EventListener);
      cancelCloseDelay();
    };
  }, [cancelCloseDelay]);

const contextValue = React.useMemo(() => ({
    sidebarOpen, 
    toggleSidebar, 
    openSidebar, 
    closeSidebar, 
    closeWithDelay, 
    cancelCloseDelay, 
    modalOpen, 
    setModalOpen, 
    paymentSidebarOpen, 
    openPaymentSidebar, 
    closePaymentSidebar, 
    paymentSidebarData,
    cartOnlyMode,
    preferredStoreId,
    preferredStoreName,
    setPreferredStoreId,
    setPreferredStoreName
  }), [
    sidebarOpen, toggleSidebar, openSidebar, closeSidebar, closeWithDelay, cancelCloseDelay, 
    modalOpen, paymentSidebarOpen, openPaymentSidebar, closePaymentSidebar, paymentSidebarData,
    cartOnlyMode, preferredStoreId, preferredStoreName
  ]);

  return (
    <CheckoutContext.Provider value={contextValue}>
      {children}
    </CheckoutContext.Provider>
  );
};

export function useCheckout() {
  const context = useContext(CheckoutContext);
  if (context === undefined) {
    throw new Error('useCheckout must be used within a CheckoutProvider');
  }
  return context;
}
